import { useRef, useState } from "react";

import { leerRegistro } from "../../services/leerRegistro";
import { comparar, ETIQUETAS, fechas, sePuedeConfirmar } from "../../shared/registroLeido";

/* Soltar el registro —foto o PDF— y que salga el vencimiento para confirmar.
 *
 * ── ENTRA UN PDF PORQUE UN ESCÁNER NO SACA FOTOS ────────────────────────────
 *
 * Marcos escanea, y lo que sale de un escáner es un PDF. Pedirle que lo pase a
 * JPG para poder soltarlo es agregarle un paso que se hace una vez y después
 * ya no — y ahí este cajón deja de usarse y se vuelve a tipear la fecha
 * mirando el papel, que es exactamente lo que vino a resolver. Del lado de
 * FlotaBot va por otro camino (bloque `document`); acá es un archivo más.
 *
 * Lo pidió Marcos, y el «Ok / Editar» que pidió ES la regla de `vale_foto.py`:
 * **se lee, se MUESTRA y se pregunta. Nunca se registra solo.** Allá un dígito
 * mal leído inventa un hueco y tapa otro; acá una fecha mal leída declara en
 * regla a un chofer que no lo está, y queda firmada con el nombre de quien
 * apretó Ok.
 *
 * ── LAS TRES FECHAS SE MUESTRAN Y UNA SOLA SE GUARDA ────────────────────────
 *
 * El registro trae otorgamiento, vencimiento y nacimiento. Leer el nacimiento
 * como vencimiento da 1975 y se ve solo; leer el OTORGAMIENTO como vencimiento
 * da una fecha creíble un par de años en el pasado, **y ésa nadie la discute**
 * — salvo que esté el otorgamiento al lado. Por eso van las tres.
 *
 * ── SIN SABER DE QUIÉN ES, NO SE PREGUNTA NADA ──────────────────────────────
 *
 * Si el documento no se leyó o no está en la lista, la tarjeta pide elegir el
 * chofer antes de ofrecer el botón. Ofrecer «Ok» sin fila a la que ir sería
 * pedir que alguien firme un papel en blanco.
 */
const fmt = (f) => {
  if (!f) return null;
  const [a, m, d] = String(f).slice(0, 10).split("-");
  return `${d}/${m}/${a}`;
};

const SoltarRegistro = ({ filas, onGuardar, onCerrar }) => {
  const [estado, setEstado] = useState("");      // "" | "leyendo" | "guardando"
  const [error, setError] = useState("");
  const [lectura, setLectura] = useState(null);
  const [dni, setDni] = useState("");
  const [vence, setVence] = useState("");
  /* LA DIRECCIÓN TAMBIÉN SE CORRIGE ANTES DEL OK. Lo pidió Marcos.
   *
   * Se mostraba y no se podía tocar, así que una calle mal leída había que
   * guardarla mal y arreglarla después en la tabla — o no arreglarla nunca.
   * Es la misma regla que ya valía para la fecha: se lee, se MUESTRA y se
   * pregunta; y si lo que se muestra no se puede corregir, la pregunta es
   * «¿sí o no?» sobre algo que se sabe mal. */
  const [dir, setDir] = useState("");
  const [editando, setEditando] = useState(false);
  const [encima, setEncima] = useState(false);
  const input = useRef(null);
  const campoDni = useRef(null);

  const fila = (filas || []).find((f) => String(f.dni) === String(dni)) || null;
  const leyendo = estado === "leyendo";

  const tomar = async (archivo) => {
    /* DOS LECTURAS A LA VEZ NO SE PUEDEN. La llamada tarda lo suyo, y sin este
     * corte un segundo clic durante la espera larga dos y gana la que conteste
     * última — que puede ser la del archivo viejo. */
    if (leyendo) return;
    setError("");
    setLectura(null);
    setEditando(false);
    setEstado("leyendo");
    const r = await leerRegistro(archivo);
    setEstado("");
    if (!r.ok) {
      setError(r.mensaje);
      return;
    }
    setLectura(r.lectura);
    setDni(r.lectura.dni || "");
    setVence(r.lectura.vence || "");
    setDir(r.lectura.direccion || "");
    /* SI FALTA CUALQUIERA DE LOS DOS, SE ABRE EN MODO EDICIÓN.
     *
     * Miraba SÓLO el vencimiento, y ése era el agujero: cuando el modelo leía
     * la fecha pero no el documento, `editando` quedaba en false, el renglón
     * del DNI se dibujaba como texto de sólo lectura, y el aviso de abajo
     * decía «escribilo en DNI, acá arriba» **señalando algo que no era un
     * campo**. Exactamente el «no podés escribir en ningún lado» que se venía
     * reportando, con el cartel nuevo empeorándolo: antes por lo menos no
     * prometía una casilla.
     *
     * Son los dos datos que la tarjeta necesita para poder guardar, así que
     * que falte cualquiera es motivo para abrir. */
    if (!r.lectura.vence || !r.lectura.dni) setEditando(true);
    /* Y EL CURSOR VA AL DNI, que es el que frena: sin saber de quién es no hay
     * fila a la que ir. El `setTimeout` espera al render — antes de él el
     * input no existe y `focus()` no tiene a qué agarrarse, que es justo lo
     * que pasaba en este caso. */
    if (!r.lectura.dni) setTimeout(() => campoDni.current?.focus(), 0);
  };

  /* EL INPUT SE VACÍA DESPUÉS DE CADA ARCHIVO, y no es prolijidad.
   *
   * `change` sólo dispara si el valor CAMBIA, así que elegir el mismo archivo
   * dos veces seguidas no dispara nada: se suelta un escaneo, sale mal, se
   * aprieta Descartar, se vuelve a soltar EL MISMO y la caja no hace
   * absolutamente nada. No falla, no avisa — se ve como una caja rota, que es
   * justo lo que se reportó. Y es el caso más probable de todos: el segundo
   * intento es siempre con el mismo papel. */
  const desdeElSelector = (e) => {
    const archivo = e.target.files?.[0];
    e.target.value = "";
    if (archivo) tomar(archivo);
  };

  const enSoltar = (e) => {
    e.preventDefault();
    setEncima(false);
    const archivo = e.dataTransfer?.files?.[0];
    if (archivo) tomar(archivo);
  };

  /* Lo que se va a escribir se calcula sobre lo EDITADO, no sobre lo leído:
   * si alguien corrigió la fecha, el aviso de «esto pisa lo que había» tiene
   * que hablar de la fecha corregida. */
  const editada = lectura
    ? { ...lectura, vence: vence || null, direccion: dir || null }
    : null;
  const { cambios, pisa, noSeToca } = editada
    ? comparar(editada, fila || {})
    : { cambios: {}, pisa: [], noSeToca: [] };
  const listo = sePuedeConfirmar(editada, fila ? dni : "");

  const guardar = async () => {
    setEstado("guardando");
    setError("");
    try {
      await onGuardar(dni, cambios);
      setLectura(null);
      setDni("");
      setVence("");
      setDir("");
      onCerrar?.();
    } catch (e) {
      setError(e?.message || "No se pudo guardar.");
    } finally {
      setEstado("");
    }
  };

  return (
    <div className="soltar-registro">
      {/* La zona de soltar sigue existiendo con la tarjeta abierta: soltar otra
          foto encima es la forma natural de decir «ésa no era». */}
      <div
        className={"sr-caja" + (encima ? " encima" : "") + (leyendo ? " leyendo" : "")}
        onDragOver={(e) => {
          e.preventDefault();
          if (!leyendo) setEncima(true);
        }}
        /* Sólo cuando se sale DE LA CAJA, no al pasar de un hijo a otro:
           `dragleave` burbujea desde el texto de adentro, y sin esto el borde
           resaltado parpadea mientras se mueve el archivo por encima — que se
           lee como que la caja no lo va a aceptar. */
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setEncima(false);
        }}
        onDrop={leyendo ? (e) => e.preventDefault() : enSoltar}
        onClick={() => !leyendo && input.current?.click()}
        role="button"
        aria-busy={leyendo}
        tabIndex={0}
        /* Enter Y ESPACIO. Un `role="button"` tiene que contestar a las dos
           teclas; con sólo Enter, el que llega con Tab aprieta espacio, la
           página se desplaza y la caja no hace nada. */
        onKeyDown={(e) => {
          if (leyendo) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            input.current?.click();
          }
        }}
      >
        {/* EL PDF VA NOMBRADO Y NO SÓLO EN `image/*`. Un escáner no saca fotos:
            con el filtro en imágenes, el que abre el selector con su escaneo
            adelante ve el PDF en gris y concluye que no se puede. */}
        <input
          ref={input}
          type="file"
          accept="image/*,application/pdf,.pdf"
          hidden
          onChange={desdeElSelector}
        />
        {leyendo ? (
          <b>Leyendo el registro…</b>
        ) : (
          <>
            <b>Soltá acá el registro</b>
            <span className="sub">
              la foto o el escaneo · JPG, PNG, WEBP o PDF
            </span>
          </>
        )}
      </div>

      {/* «No pude leerlo» no deja a nadie sin salida: la fecha se sigue
          pudiendo escribir a mano en la fila del chofer, que es como se cargaba
          antes de que este cajón existiera. Sin decirlo, un error acá se lee
          como que hoy no se puede cargar ese vencimiento. */}
      {error && (
        /* `suave`: sin el modificador esto es un overlay absoluto que tapa la
           pantalla entera, y el archivo ya lo usa así unas líneas abajo. */
        <p className="panel-aviso suave">
          {error}{" "}
          <span className="tk-item-meta">
            Podés soltar otro archivo, o cargar la fecha a mano en la fila del
            chofer.
          </span>
        </p>
      )}

      {lectura && (
        <div className="sr-tarjeta">
          <h4>Esto entendí</h4>

          {/* LAS TRES FECHAS JUNTAS. Sólo se guarda el vencimiento; las otras
              dos están para que la equivocada se note. */}
          <ul className="sr-campos">
            {fechas(lectura).map(([k, et, v]) => (
              <li key={k} className={k === "vence" ? "sr-clave" : "tenue"}>
                <span className="et">{et}</span>
                {k === "vence" && editando ? (
                  <input
                    type="date"
                    value={vence}
                    onChange={(e) => setVence(e.target.value)}
                    aria-label="Vencimiento del registro"
                  />
                ) : (
                  <span>{fmt(v) || <i className="sin-dato">no se leyó</i>}</span>
                )}
                {k === "vence" && <span className="tk-item-meta">es el que se guarda</span>}
              </li>
            ))}
            {/* SE LLAMA «DNI» Y NO «DOCUMENTO», y no es un detalle de estilo:
                el aviso de abajo pide el DNI, la columna de la tabla dice DNI,
                y este renglón decía otra cosa. Con seis renglones en la lista
                y cinco diciendo «no se leyó», el que busca dónde escribir el
                DNI no lo encuentra — le dijeron el nombre de un campo que no
                está en pantalla.

                Y `.sr-falta` lo marca cuando es EL dato que frena: sin eso hay
                que leer los seis para saber cuál es el que hay que llenar. */}
            <li className={!fila && editando ? "sr-falta" : undefined}>
              <span className="et">DNI</span>
              {editando ? (
                <input
                  ref={campoDni}
                  /* `type="text"` explícito: los estilos del hub cuelgan de
                     `input[type="text"]`, y un input SIN atributo `type` no
                     matchea ese selector. Sin él éste era el único campo de la
                     tarjeta sin borde ni fondo —el de la fecha sí los tiene,
                     porque es `type="date"`— así que no se veía como un campo
                     y por eso parecía que no se podía escribir. */
                  type="text"
                  value={dni}
                  onChange={(e) => setDni(e.target.value.replace(/\D/g, ""))}
                  inputMode="numeric"
                  placeholder="sin puntos"
                  aria-label="DNI del chofer"
                />
              ) : (
                <span>{lectura.dni || <i className="sin-dato">no se leyó</i>}</span>
              )}
            </li>
            {/* La dirección va aparte porque SE PUEDE EDITAR. Y sigue sin ser
                la zona: `localidad` es lo que completa el partido —y sólo si
                está vacío—, esto es la calle. Escribir una en la otra le saca
                la prórroga de 60 días a ese chofer sin fallar en ningún
                lado. */}
            <li>
              <span className="et">{ETIQUETAS.direccion}</span>
              {editando ? (
                <input
                  type="text"
                  value={dir}
                  onChange={(e) => setDir(e.target.value)}
                  placeholder="no se leyó"
                  aria-label="Dirección del registro"
                />
              ) : (
                <span>{dir || <i className="sin-dato">no se leyó</i>}</span>
              )}
            </li>
            {["nombre", "localidad", "clases"].map((k) => (
              <li key={k} className="tenue">
                <span className="et">
                  {ETIQUETAS[k] || (k === "localidad" ? "Localidad"
                    : k === "clases" ? "Clases" : "Nombre")}
                </span>
                <span>{lectura[k] || <i className="sin-dato">no se leyó</i>}</span>
              </li>
            ))}
          </ul>

          {/* Lo que el modelo dijo no estar seguro de leer. Va con el resto y no
              escondido: es lo que hay que mirar dos veces antes del Ok. */}
          {lectura.dudas?.length > 0 && (
            <p className="panel-aviso suave">
              ⚠️ No está seguro de: {lectura.dudas.join(" · ")}. Miralo contra la
              foto antes de guardar.
            </p>
          )}

          {/* SIN FILA NO HAY BOTÓN. «No se leyó el documento» y «ese DNI no está
              cargado» mandan a hacer cosas distintas, así que se dicen distinto. */}
          {/* Y EL AVISO NOMBRA EL CAMPO Y DICE DÓNDE ESTÁ. Decía «escribilo
              para seguir» y nada más, con el campo arriba y llamado de otra
              forma: se pide un dato y no se dice en qué caja va. */}
          {!fila && (
            <p className="panel-aviso suave">
              {dni
                ? `El DNI ${dni} no está en la lista de documentación. Agregá al chofer primero, o corregí el número en «DNI», acá arriba.`
                : "No pude leer el documento, así que no sé de quién es este registro. Escribilo en «DNI», acá arriba, para seguir."}
              {!editando && (
                <>
                  {" "}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setEditando(true);
                      /* Y el botón deja el cursor adentro del campo. Prender la
                         edición y dejar que la persona lo busque es la mitad
                         del trabajo — el `setTimeout` espera a que el input
                         exista, porque antes del render no hay a qué enfocar. */
                      setTimeout(() => campoDni.current?.focus(), 0);
                    }}
                  >
                    Escribir el DNI
                  </button>
                </>
              )}
            </p>
          )}

          {fila && (
            <p className="sr-quien">
              Se va a guardar en <b>{fila.nombre || `DNI ${fila.dni}`}</b>.
            </p>
          )}

          {/* QUÉ SE PISA, ANTES DE APRETAR Y CON EL VALOR ANTERIOR. La fecha sí
              reemplaza a la que había —el papel recién fotografiado es más
              nuevo que la planilla— pero no en silencio. */}
          {pisa.map((p) => (
            <p className="sr-pisa" key={p.campo}>
              ⚠️ <b>{ETIQUETAS[p.campo]}</b> ya decía{" "}
              {p.campo === "registro_vence" ? fmt(p.antes) : p.antes} y va a
              quedar en {p.campo === "registro_vence" ? fmt(p.ahora) : p.ahora}.
            </p>
          ))}

          {/* Y LO QUE NO SE TOCA, con el motivo. Callarlo haría que alguien crea
              que la zona quedó corregida. */}
          {noSeToca.map((n) => (
            <p className="sr-nota" key={n.campo}>
              No se cambia <b>{ETIQUETAS[n.campo] || n.campo}</b>: {n.motivo}
            </p>
          ))}

          <div className="sr-acciones">
            <button
              type="button"
              className="btn-primario"
              disabled={!listo || estado === "guardando"}
              onClick={guardar}
            >
              {estado === "guardando"
                ? "Guardando…"
                : listo
                  ? `Ok, guardar (${Object.keys(cambios).length} ${
                      Object.keys(cambios).length === 1 ? "campo" : "campos"})`
                  : "Ok, guardar"}
            </button>
            {!editando && (
              <button type="button" className="btn-suave"
                      onClick={() => setEditando(true)}>
                Editar
              </button>
            )}
            <button type="button" className="btn-suave" onClick={() => {
              setLectura(null);
              setError("");
            }}>
              Descartar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoltarRegistro;
